import { X } from 'lucide-react';
import { useRef } from 'react';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { COUNTRIES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import {
  isValidLinkedInProfileUrl,
  normalizeLinkedInProfileUrl,
} from '@/lib/validations/social-profiles';
import type { LinkedinCheckState } from '../use-linkedin-check';
import { LinkedinCheckStatus } from './LinkedinCheckStatus';
import type { ProfileFormData } from './types';

interface Props {
  ownerName: string;
  data: ProfileFormData;
  onChange: (patch: Partial<ProfileFormData>) => void;
  /** The server-side LinkedIn verification, for the editions that run it. */
  linkedinCheck?: (LinkedinCheckState & { retry: () => void }) | null;
}

export function PersonalStep({
  ownerName,
  data,
  onChange,
  linkedinCheck,
}: Props) {
  const linkedinInvalid =
    data.linkedin.trim().length > 0 &&
    !isValidLinkedInProfileUrl(normalizeLinkedInProfileUrl(data.linkedin));
  const linkedinRejected =
    linkedinCheck != null &&
    ['invalid', 'taken', 'not_found', 'failed'].includes(linkedinCheck.status);
  // A verified profile is locked; the X clears it so another URL can be typed.
  const linkedinLocked = linkedinCheck?.status === 'found';
  const linkedinInput = useRef<HTMLInputElement>(null);

  const clearLinkedin = () => {
    onChange({ linkedin: '' });
    // After the re-render that unlocks the field.
    requestAnimationFrame(() => linkedinInput.current?.focus());
  };

  return (
    <div className="space-y-6">
      <h2 className="font-bold text-3xl">{ownerName}.inspect</h2>

      <div className="space-y-6">
        <div>
          <div className="mb-2 block font-semibold text-sm">país *</div>
          <Combobox
            options={COUNTRIES.map((country) => ({
              value: country.code,
              label: country.name,
              emoji: country.emoji,
            }))}
            firstOptionValue="CO"
            value={data.country}
            onValueChange={(value) => onChange({ country: value })}
            placeholder="select country..."
            searchPlaceholder="search countries..."
            emptyText="no country found."
          />
        </div>

        <div>
          <div className="mb-2 block font-semibold text-sm">
            perfil de linkedin *
          </div>
          <div className="relative">
            <Input
              ref={linkedinInput}
              type="text"
              placeholder="https://linkedin.com/in/username"
              value={data.linkedin}
              onChange={(e) => onChange({ linkedin: e.target.value })}
              readOnly={linkedinLocked}
              aria-invalid={linkedinInvalid || linkedinRejected}
              className={cn(
                'border-border bg-card text-foreground',
                linkedinLocked && 'cursor-default pr-10 text-muted-foreground',
              )}
            />
            {linkedinLocked && (
              <button
                type="button"
                onClick={clearLinkedin}
                aria-label="Cambiar perfil de LinkedIn"
                title="Cambiar perfil de LinkedIn"
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          {linkedinInvalid && (
            <p className="mt-2 text-destructive text-xs">
              Ingresa la URL de tu perfil, ej:
              https://linkedin.com/in/tu-usuario
            </p>
          )}
          {!linkedinInvalid && linkedinCheck && (
            <LinkedinCheckStatus check={linkedinCheck} />
          )}
        </div>

        <div>
          <div className="mb-2 block font-semibold text-sm">perfil de x</div>
          <p className="mb-2 text-secondary-foreground text-xs">opcional</p>
          <Input
            type="text"
            placeholder="https://x.com/username"
            value={data.x}
            onChange={(e) => onChange({ x: e.target.value })}
            className="border-border bg-card text-foreground"
          />
        </div>
      </div>
    </div>
  );
}
